import { useEffect, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { PondingLog } from '@/components/flood-events/types';

export function usePondingLogs(eventId?: string | null) {
  const [pondingLogs, setPondingLogs] = useState<PondingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadPondingLogs() {
      try {
        // Build query based on whether eventId is provided
        const pondingsRef = collection(db, 'ponding-logs');
        const q = eventId 
          ? query(pondingsRef, where('eventId', '==', eventId))
          : pondingsRef;
        
        const pondingsSnap = await getDocs(q);
        if (!mounted) return;
        
        const allPondings = pondingsSnap.docs.map(d => ({ 
          id: d.id, 
          ...d.data() 
        })) as PondingLog[];

        setPondingLogs(allPondings);
        setError(null);
      } catch (err) {
        console.error('Failed to load ponding logs', err);
        if (mounted) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPondingLogs();

    // Set up real-time listener for updates
    const pondingsRef = collection(db, 'ponding-logs');
    const q = eventId 
      ? query(pondingsRef, where('eventId', '==', eventId))
      : pondingsRef;
      
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (mounted) {
          const allPondings = snapshot.docs.map(d => ({ 
            id: d.id, 
            ...d.data() 
          })) as PondingLog[];
          setPondingLogs(allPondings);
        }
      },
      (err) => {
        console.error('Ponding logs subscription error:', err);
        if (mounted) setError(err.message);
      }
    );

    return () => { 
      mounted = false;
      unsubscribe();
    };
  }, [eventId]); // Re-run when eventId changes

  return { pondingLogs, loading, error };
}
